import {
  AppBar,
  Button,
  Container,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  Menu,
  MenuItem,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import React, { useState, useEffect, useRef } from "react";
import logoLight from "../../../../assets/images/mmm-white-logo.png";
import { useNavigate, useLocation } from "react-router-dom";
import { logoutUser } from "../../../../graphql/mutations/logoutUser";
import { useMutation } from "@apollo/client";
import AccountCircleIcon from "@mui/icons-material/PersonOutline";
import ArrowDropDownIcon from "@mui/icons-material/KeyboardArrowDownOutlined";
import ArrowDropUpIcon from "@mui/icons-material/KeyboardArrowUpOutlined";
import CloseIcon from "@mui/icons-material/Close";
import useStepper from "../../../../hooks/useStepper";
import {
  dialogName,
  TRACKING_HEADER_OPTIONS,
  userRoles,
} from "../../../../constants";
import {
  getLegacyAppURL,
  legacyAppRedirection,
  mmmUniversalRedirection,
  parameterizedDetail,
  removeCookies,
} from "../../../../helpers";
import useUserEvent from "../../../../hooks/useUserEvent";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { isNHSApplication } from "../../../../reducers/multiFormReducer";
import MenuIcon from "@mui/icons-material/Menu";
import { newApplicationFormPath } from "../../../../constants/path";
import usePlan from "../../../../hooks/usePlan";
import LogoutButton from "./LogoutButton";
import { debounce } from "lodash";
import useDialog from "../../../../hooks/useModal";
import SaveChangesalert from "./SaveChangesalert";
import Signout from "../../../../assets/images/sign-out.svg";
import hoverSignOut from "../../../../assets/images/hover-sign-out.png";
import HideNewHeaderOnScroll from "../../../../hooks/HideNewHeaderOnScroll";

function NewLoggedinHeader({ organisation, user, user_roles_organisation }) {
  const [logout] = useMutation(logoutUser);

  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname;
  const [anchorEl, setAnchorEl] = useState(null);
  const [isLoading, setLoading] = useState(false);
  const [mobileNavEl, setMobileNavEl] = useState(null);
  const { personalDetails, trackingCode } = useStepper();
  const { userTrackingMutation } = useUserEvent();
  const { isLatestActivePlan } = usePlan();
  const menuContainerRef = useRef(null);
  const [open, setOpen] = useState(false);
  const { dialogs, openDialog, closeDialog } = useDialog();
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    const handleResize = debounce(() => {
      if (menuContainerRef?.current) {
        setAnchorEl(menuContainerRef?.current);
        setOpen(false);
        setMobileNavEl(null);
      }
    }, 100);

    handleResize();

    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
      handleResize.cancel();
    };
  }, [menuContainerRef]);

  const isTablet = useMediaQuery("(max-width: 991px)");
  const isMobile = useMediaQuery("(max-width: 600px)");

  const mobileOpen = Boolean(mobileNavEl);
  const handleClick = (event) => {
    if (event) setAnchorEl(event?.currentTarget);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleClickMobileNav = (event) => {
    setMobileNavEl(event.currentTarget);
  };

  const handleCloseMobileNav = () => {
    setMobileNavEl(null);
  };

  const handleOpenDialog = () => {
    const IS_NEW_APPLICATION_PATH = [
      newApplicationFormPath.NEW_APPLICATION_FORM,
      newApplicationFormPath.NHS_APPLICATION_FORM,
    ].includes(currentPath);

    if (isLatestActivePlan || !IS_NEW_APPLICATION_PATH) {
      setLoading(true);
      logout({
        onCompleted: (data) => {
          removeCookies();
          window.location.href = data?.destroy_session?.logout_url;
        },
        onError: () => {
          setLoading(false);
          removeCookies();
          legacyAppRedirection(personalDetails?.sub_domain, "/");
        },
      });
      return;
    }
    openDialog(dialogName.SAVE_CHANGES);
    setOpen((prevOpen) => !prevOpen);
  };

  const handleCloseDialog = () => {
    closeDialog(dialogName.SAVE_CHANGES);
  };

  const legacyAppURL = getLegacyAppURL(personalDetails?.sub_domain);
  const adminBaseURL = `${legacyAppURL}/organisations/${parameterizedDetail(
    organisation?.id,
    organisation?.name
  )}`;

  const topMenu = () => {
    if (personalDetails.role_name === userRoles.ORGANISATION_EMPLOYEE) {
      const menus = [
        { name: "Dashboard", link: "/employee/dashboard", mmm: true },
        {
          name: "Live Sessions",
          link: "/employee/liveSessions/list",
          mmm: true,
        },
        { name: "Products", link: "/employee/products", mmm: true },
      ];
      return menus.map((menu) => ({
        ...menu,
        isActive: currentPath === menu.link,
      }));
    } else {
      const orgAdminMenu = [
        {
          name: "Dashboard",
          link: `${adminBaseURL}/dashboard`,
          target: "_self",
        },
        {
          name: "Application Management",
          link: `${adminBaseURL}/plans_management`,
          target: "_self",
        },

        {
          name: "Audit History",
          link: `${adminBaseURL}/audit_history`,
          target: "_self",
        },

        { name: "Settings", link: `${adminBaseURL}/settings`, target: "_self" },
      ];
      return orgAdminMenu.map((menu) => ({
        ...menu,
        isActive: currentPath === menu.link,
      }));
    }
  };

  const ProfileMenu = () => {
    const getRoleChangeMenuItem = () => {
      const hasMultipleOrgs = user_roles_organisation?.length > 1;
      const isBoth = user?.is_employer_and_employee;
      const isBackend = user?.backend_user;

      if (isBackend || (isBoth && hasMultipleOrgs)) {
        return {
          name: "Change Organisation/Role",
          link: "/change_portal",
        };
      }

      if (isBoth) {
        return {
          name: "Change Role",
          link: "/change_portal",
        };
      }

      if (hasMultipleOrgs) {
        return {
          name: "Change Organisation",
          link: "/change_portal",
        };
      }

      return null;
    };
    const baseMenu = [
      {
        name: "Help & Contact",
        link: "/employee/contact-us",
        mmm: true,
      },
      {
        name: "My Profile",
        link: "/employee/profile",
        mmm: true,
      },
    ];
    if (personalDetails.role_name === userRoles.ORGANISATION_EMPLOYEE) {
      const roleChangeItem = getRoleChangeMenuItem();
      if (roleChangeItem) {
        baseMenu.push(roleChangeItem);
      }
      return baseMenu.map((menu) => ({
        ...menu,
        isActive: currentPath === menu.link,
      }));
    } else {
      const roleChangeItem = getRoleChangeMenuItem();
      if (roleChangeItem) {
        baseMenu.push(roleChangeItem);
      }
      return baseMenu.map((menu) => ({
        ...menu,
        isActive: currentPath === menu.link,
      }));
    }
  };

  const handleClosePopover = () => {
    handleCloseMobileNav();
    setIsHovered(false);
    setOpen(false);
  };

  useEffect(() => {
    if (!open) {
      setIsHovered(false);
    }
  }, [open]);

  return (
    <>
      <AppBar className={`top-new-header visible`}>
        <HideNewHeaderOnScroll
          handleClosePopover={handleClosePopover}
          headerClass={".top-new-header"}
        />
        <Container
          className="custom-container"
          sx={{
            paddingRight: { xs: "1.25rem", md: "0px !important" },
            paddingLeft: { xs: "1.25rem", md: "2rem" },
          }}
          maxWidth="none"
        >
          <Grid container className="custom-row">
            <Grid xs={12} className="custom-col">
              <div
                className="navbar"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <div
                  className="navbar-brand "
                  style={{ display: "flex", alignItems: "center" }}
                >
                  <Button
                    padding={0}
                    onClick={() => navigate("/home")}
                    aria-label="myMoneyMatters"
                  >
                    <img
                      src={logoLight}
                      className="img-fluid light-logo"
                      alt="MMM"
                    />
                  </Button>
                </div>
                {personalDetails && personalDetails.role_id && isTablet && (
                  <Stack
                    display={"flex"}
                    alignItems={"center"}
                    gap={1}
                    flexDirection={"row"}
                    height={"100%"}
                  >
                    <IconButton
                      color="inherit"
                      aria-label="open drawer"
                      id="menu-button"
                      edge="start"
                      className={`hamburger-menu mobile-menu`}
                      padding={0}
                      display={{ xs: "block", md: "none" }}
                      onClick={(e) => handleClick(e)}
                    >
                      {!open ? (
                        <AccountCircleIcon
                          sx={{
                            color: "#C06DF3",
                            borderRadius: "50%",
                            background: "white",
                            width: "30.9px",
                            height: "30.9px",
                            padding: "6.18px",
                            transition: "background 0.3s ease",
                          }}
                        />
                      ) : (
                        <CloseIcon
                          sx={{
                            color: "white",
                            borderRadius: "50%",
                            background: "#FC89E7",
                            width: "30.9px",
                            height: "30.9px",
                            padding: "6.18px",
                            transition: "background 0.3s ease",
                          }}
                        />
                      )}
                    </IconButton>
                    <IconButton
                      color="inherit"
                      aria-label="open drawer"
                      id="menu-button"
                      edge="start"
                      className={`hamburger-menu mobile-menu`}
                      display={{ xs: "block", md: "none" }}
                      padding={0}
                      onClick={(e) => handleClickMobileNav(e)}
                    >
                      {!mobileOpen ? (
                        <MenuIcon
                          sx={{
                            width: "24px",
                            height: "24px",
                          }}
                        />
                      ) : (
                        <CloseIcon
                          sx={{
                            color: "white",
                            borderRadius: "50%",
                            background: "#FC89E7",
                            width: "30.9px",
                            height: "30.9px",
                            padding: "6.18px",
                            transition: "background 0.3s ease",
                          }}
                        />
                      )}
                    </IconButton>
                  </Stack>
                )}
                {personalDetails && personalDetails.role_id ? (
                  <div className={`navbar-menu-wrapper`}>
                    {/* <div className="close-menu d-flex d-lg-none align-items-center justify-content-between">
                    <Button
                      className="p-0"
                      onClick={() => navigate("/home")}
                      aria-label="myMoneyMattersLogo"
                    >
                      <img
                        src={logoDark}
                        className="img-fluid dark-logo"
                        alt=""
                      />
                    </Button>
                    <a aria-label="mobileMenu">
                      <i className="mmm-cancel"></i>
                    </a>
                  </div> */}
                    <div className="navbar-menus">
                      <div className="navbar-menu-left">
                        <List
                          className="nav-list"
                          display={{ xs: "", md: "flex" }}
                          alignItems={{ xs: "", md: "center" }}
                          paddingY={0}
                        >
                          {topMenu().map((res) => (
                            <ListItem
                              key={res.link}
                              className="nav-item"
                              disablePadding
                              onClick={(e) => {
                                userTrackingMutation({
                                  variables: {
                                    ...TRACKING_HEADER_OPTIONS,
                                    field_name: e?.target?.type || name || "",
                                    field_value:
                                      e?.target?.textContent?.toString() || "",
                                    avc_track_code: trackingCode,
                                  },
                                });
                              }}
                            >
                              <div
                                onClick={() => {
                                  if (res?.mmm) {
                                    mmmUniversalRedirection(
                                      personalDetails?.sub_domain,
                                      `${res.link}`
                                    );
                                  } else if (res?.target) {
                                    window.open(res.link, res.target);
                                  } else {
                                    navigate(res.link);
                                  }
                                }}
                                className={
                                  res?.isActive ? "nav-link active" : "nav-link"
                                }
                              >
                                <span>{res.name}</span>
                              </div>
                            </ListItem>
                          ))}
                        </List>
                      </div>
                      <div
                        className="navbar-menu-right"
                        style={{ display: "flex", alignItems: "center" }}
                        ref={menuContainerRef}
                        id="menu-lists"
                      >
                        {/* Vertical Divider */}
                        <Divider
                          orientation="vertical"
                          sx={{
                            borderColor: "#9B53D2",
                            borderWidth: 1,
                            height: "81px",
                          }}
                        />

                        {/* More Options Button */}
                        <Stack
                          display={"flex"}
                          alignItems={"center"}
                          gap={1}
                          flexDirection={"row"}
                        >
                          <Button
                            sx={{
                              textTransform: "none",
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                              fontSize: "14px",
                              fontWeight: "400",
                              color: isHovered || open ? "#FC89E7" : "white",
                            }}
                            endIcon={
                              open ? (
                                <ArrowDropUpIcon
                                  sx={{ fontSize: "1rem", color: "white" }}
                                />
                              ) : (
                                <ArrowDropDownIcon
                                  sx={{ fontSize: "1rem", color: "white" }}
                                />
                              )
                            }
                            onMouseEnter={() => setIsHovered(true)}
                            onMouseLeave={() => setIsHovered(false)}
                            onClick={() => {
                              handleClick();
                            }}
                          >
                            <Typography
                              color={isHovered || open ? "#FC89E7" : "white"}
                              fontSize="1.375rem"
                              fontWeight={400}
                              sx={{
                                cursor: "pointer",
                                "&:hover": {
                                  color: "#FC89E7",
                                },
                              }}
                            >
                              More Options
                            </Typography>
                          </Button>

                          {/* Profile Icon Button */}
                          <IconButton padding={0}>
                            <AccountCircleIcon
                              sx={{
                                color: open || isHovered ? "white" : "#C06DF3",
                                borderRadius: "50%",
                                background:
                                  open || isHovered ? "#FC89E7" : "white",
                                width: "50px",
                                height: "50px",
                                padding: "10px",
                                transition: "background 0.3s ease",
                              }}
                            />
                          </IconButton>
                        </Stack>
                      </div>
                    </div>
                  </div>
                ) : (
                  <Grid item xs={6} md={10}>
                    <Stack
                      direction="row"
                      justifyContent="flex-end"
                      paddingRight={{ xs: 0, md: "24px" }}
                    >
                      <LogoutButton />
                    </Stack>
                  </Grid>
                )}
              </div>

              <Menu
                id="mobile-menu"
                anchorEl={mobileNavEl}
                open={mobileOpen}
                onClose={handleCloseMobileNav}
                className="dropdown-new-menu"
                container={document.body}
                disablePortal={false}
                sx={{
                  ".MuiPopover-paper": {
                    width: "100%",
                    maxWidth: "none",
                    borderTop: "1px solid #7236AE",
                    padding: "24px 16px",
                    display: "flex",
                    flexDirection: "column",
                    minWidth: "200px",
                    position: "fixed !important",
                    top: "5rem !important",
                  },
                }}
                MenuListProps={{
                  "aria-labelledby": "menu-button",
                }}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "center",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "center",
                }}
              >
                {topMenu().map((res) => (
                  <MenuItem
                    key={res.name}
                    disableRipple
                    onClick={() => {
                      handleCloseMobileNav();
                      if (res?.mmm) {
                        mmmUniversalRedirection(
                          personalDetails?.sub_domain,
                          `${res.link}`
                        );
                      } else if (res?.target) {
                        window.open(res.link, res.target);
                      } else {
                        navigate(res.link);
                      }
                    }}
                    className={res.isActive ? "active" : ""}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      columnGap: "12px",
                      fontSize: "14px",
                      fontWeight: "500",
                      color: "#4A2679", // $dark-purple-400
                      "&:hover": {
                        color: "#9B53D2", // $purple-2
                        backgroundColor: "transparent",
                      },
                    }}
                  >
                    <span>{res.name}</span>
                  </MenuItem>
                ))}
              </Menu>

              {/* Dropdown Menu */}
              <Menu
                id="profile-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                className="dropdown-new-menu"
                disableScrollLock={isTablet ? false : true}
                sx={{
                  ".MuiPopover-paper": {
                    position: "fixed !important",
                    width: isTablet
                      ? "100%"
                      : anchorEl
                      ? `${anchorEl.offsetWidth + 1}px`
                      : "auto",
                    marginTop: "0px !important",
                    borderTop: isTablet ? "1px solid #7236AE" : "none",
                    padding: isMobile
                      ? "0px 40px !important"
                      : "0px 37px 22px 27px !important",
                    display: "flex",
                    flexDirection: "column",
                    rowGap: "16px",
                    minWidth: "200px",
                    left: "unset !important",
                    right: "0 !important",
                    top: isMobile ? "5rem !important" : "107px !important",
                    ul: {
                      padding: isMobile ? "20px 0px !important" : "0px",
                    },
                  },
                }}
                MenuListProps={{
                  "aria-labelledby": "basic-button",
                }}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "center",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "center",
                }}
              >
                {/* Dynamic Profile Menu Items */}
                {ProfileMenu().map((res) => (
                  <MenuItem
                    key={res.name}
                    disableRipple
                    onClick={() => {
                      handleClose();
                      if (res?.mmm) {
                        mmmUniversalRedirection(
                          personalDetails?.sub_domain,
                          `${res.link}`
                        );
                      } else if (res?.target) {
                        window.open(res.link, res.target);
                      } else {
                        navigate(res.link);
                      }
                    }}
                    className={res.isActive ? "active" : ""}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      columnGap: "12px",
                      fontSize: "14px",
                      fontWeight: "500",
                      color: "#4A2679", // $dark-purple-400
                      "&:hover": {
                        color: "#9B53D2", // $purple-2
                        backgroundColor: "transparent",
                      },
                    }}
                  >
                    <span>{res.name}</span>
                  </MenuItem>
                ))}

                {/* Divider */}
                <Divider className="dropdown-divider" />

                {/* Sign Out */}
                <MenuItem
                  disableRipple
                  onClick={() => {
                    handleOpenDialog();
                  }}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between !important",
                    fontSize: isMobile ? "26px !important" : "22px !important",
                    fontWeight: "400 !important",
                    padding: isMobile
                      ? "24px 0px !important"
                      : "12px 0px !important",
                    cursor: "pointer",
                    "& .hover-icon": {
                      display: "none",
                    },
                    "&:hover .default-icon": {
                      display: "none",
                    },
                    "&:hover .hover-icon": {
                      display: "inline",
                    },
                  }}
                >
                  <span>{isLoading ? "Signing out" : "Sign out"}</span>
                  <span className="default-icon">
                    <img src={Signout} alt="sign-out" />
                  </span>
                  <span className="hover-icon">
                    <img src={hoverSignOut} alt="sign-out" />
                  </span>
                </MenuItem>
              </Menu>
            </Grid>
          </Grid>

          <SaveChangesalert
            opendialog={dialogs?.includes(dialogName.SAVE_CHANGES)}
            removeCookies={() => removeCookies()}
            handleClosedialog={handleCloseDialog}
          />
        </Container>
      </AppBar>
    </>
  );
}

const mapStateToProps = (state) => {
  return {
    user: state.user.user,
    scheme: state.scheme.scheme,
    organisation: state.organisation.organisation,
    sharedTitle: state.multiForm.sharedTitle,
    isNHS: isNHSApplication(state),
    user_roles_organisation: state.organisation.user_roles_organisations,
  };
};

NewLoggedinHeader.propTypes = {
  user: PropTypes.object,
  organisation: PropTypes.object,
  scheme: PropTypes.object,
  sharedTitle: PropTypes.string,
  isNHS: PropTypes.bool,
  user_roles_organisation: PropTypes.array,
};

export default connect(mapStateToProps)(NewLoggedinHeader);
